import React, { useState } from 'react';
import { Text } from '@gjensidige/builders-components';
import {
  CalculationWidget,
  CalculationWidgetProps
} from '@gjensidige/se-common-calculation';
import { useFormValidation } from '@gjensidige/se-form-validation';
import type { FieldType, FieldValidation, ValidationRule } from '@gjensidige/se-form-validation';

import { checkEligibility, fetchPropertyData } from '../api-config/index';
import { CustomErrorModal } from '../components/CustomErrorModal';
import { PropertyIdExplanationModal } from '../components/PropertyIdExplanationModal';
import { WidgetProps } from '../interfaces/Types';
import { AppHelper } from '../utils/AppHelper';
import {
  collapsePropertyIdWhitespace,
  collapseWhitespace,
  removeKommunPostfix
} from '../utils/CalculationHelper';
import { logInfoMessage } from '../utils/LogHelper';
interface InputWidgetProps {
  enonicData: WidgetProps;
}

export const InputWidgetContainer: React.FC<InputWidgetProps> = ({
  enonicData
}: InputWidgetProps) => {
  const [formData, setFormData] = useState({
    ssn: '',
    zipCode: '',
    address: '',
    propertyKey: '',
    municipality: '',
    phone: ''
  });

  const MIN_AGE = 18

  const [addressType, setAddressType] = useState(AppHelper.streetAddress);
  const [isPropertyDataLoading, setIsPropertyDataLoading] = useState(false);
  const [isCprValidationLoading, setIsCprValidationLoading] = useState(false);

  // Modals
  const [propertyIdExplanationModalOpen, setPropertyIdExplanationModalOpen] = useState(false);
  const [hasCustomerError, setHasCustomerError] = useState(false);
  const [errorObject, setErrorObject] = useState({
    error: false,
    title: AppHelper.defaultErrorTitle,
    message: AppHelper.defaultErrorMessage,
    statusCode: AppHelper.defaultErrorStatusCode
  });

  const fieldDefinitions = [
    {
      fieldType: 'ssn' as FieldType,
      fieldName: 'Personnummer',
      validation: {
        rules: ['required', 'ssn', 'ssnAge'] as ValidationRule[],
        validationParams: { minAge: MIN_AGE },
        validateOnChange: true
      },
      ui: {
        id: 'cpr-number',
        labelText: enonicData.cprNumberLabel,
        name: 'input-cprNumber',
        helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
        isHelpTextAlwaysVisible: true,
        className: 'mb-1',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'ssn',
        inputMode: 'numeric' as const
      }
    },
    {
      fieldType: 'address' as FieldType,
      fieldName: 'Adress',
      validation: {
        rules: ['address'] as ValidationRule[]
      },
      ui: {
        id: 'gatuaddress',
        labelText: enonicData.addressLabel,
        name: 'input-gatuaddress',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'street-address',
        autoCompleteLabel: 'street-address'
      }
    },
    {
      fieldType: 'zipCode' as FieldType,
      fieldName: 'Postnummer',
      validation: {
        rules: ['zipCode'] as ValidationRule[]
      },
      ui: {
        id: 'post-number',
        labelText: enonicData.zipCodeLabel,
        name: 'postNumber',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'zip-code',
        inputMode: 'numeric' as const,
        autoCompleteLabel: 'postal-code'
      }
    },
    {
      fieldType: 'municipality' as FieldType,
      fieldName: 'Kommun',
      validation: {
        rules: ['municipality'] as ValidationRule[]
      },
      ui: {
        id: 'kommun',
        labelText: enonicData.municipalityLabel,
        name: 'input-kommun',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'municipality',
        autoCompleteLabel: 'municipality'
      }
    },
    {
      fieldType: 'propertyKey' as FieldType,
      fieldName: 'Fastighetsbeteckning',
      validation: {
        rules: ['propertyKey'] as ValidationRule[]
      },
      ui: {
        id: 'fastighetsbetackning',
        labelText: enonicData.propertyNameLabel,
        name: 'input-fastighetsbetackning',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'property-id',
        helpText: 'T.ex. "Pålsjö 1:24"',
        isHelpTextAlwaysVisible: true,
        inputHelperLinkText: enonicData.propertyNameHelpText,
        inputHelpTextId: 'property-id-more-info',
        autoCompleteLabel: 'property-name',
        inputHelperLinkOnClick: () => setPropertyIdExplanationModalOpen(true)
      }
    },
    {
      fieldType: 'phone' as FieldType,
      fieldName: 'Telefonnummer',
      validation: {
        rules: ['phone'] as ValidationRule[]
      },
      ui: {
        id: 'phone-number',
        labelText: 'Mobil (frivilligt)',
        name: 'input-phoneNumber',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'phone-number',
        helpText: AppHelper.phoneNrHelpText,
        helpTextToggleLabel: 'Visa hjälptext för Mobil',
        helpTextCloseLabel: 'Stäng hjälptext för Mobil',
        inputMode: 'numeric' as const,
        autoCompleteLabel: 'tel'
      }
    }
  ] as const;

  const getValidationRules = (fieldType: FieldType): ValidationRule[] => {
    const baseRules = fieldDefinitions.find(f => f.fieldType === fieldType)?.validation.rules || [];

    if (!baseRules.includes('required')) {
      // Address and zipCode are required only for street address
      if ((fieldType === 'address' || fieldType === 'zipCode') && addressType === AppHelper.streetAddress) {
        return ['required', ...baseRules] as ValidationRule[];
      }

      // PropertyKey and municipality are required only for property ID
      if ((fieldType === 'propertyKey' || fieldType === 'municipality') && addressType === AppHelper.propertyId) {
        return ['required', ...baseRules] as ValidationRule[];
      }
    }

    return baseRules;
  };

  const fieldConfigs: FieldValidation[] = fieldDefinitions.map(field => ({
    fieldType: field.fieldType,
    fieldName: field.fieldName,
    rules: getValidationRules(field.fieldType),
    ...('validationParams' in field.validation && field.validation.validationParams && {
      validationParams: field.validation.validationParams,
    }),
    ...('validateOnChange' in field.validation && field.validation.validateOnChange && {
      validateOnChange: field.validation.validateOnChange,
    })
  }));

  const {
    refs,
    handleBlur,
    handleChange,
    validateAll,
    getFieldError,
    hasFieldError,
  } = useFormValidation(fieldConfigs);

  const createChangeHandler = (fieldType: FieldType) => (event: any) => {
    event.preventDefault();
    let value = event.currentTarget.value;

    // Field-specific processing
    if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'zipCode') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'phone') {
      value = value.trim();
    } else if (fieldType === 'municipality') {
      value = value.replace('.', '');
    }

    setFormData(prev => ({ ...prev, [fieldType]: value }));

    if (fieldType === 'ssn') {
      const validationResult = handleChange(fieldType, value);
      if (validationResult.isValid) {
        handleCprValidation(value);
      }
    } else {
      handleChange(fieldType, value);
    }
  };

  const createBlurHandler = (fieldType: FieldType) => (event: any) => {
    event.preventDefault();
    let value = event.currentTarget.value;

    if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'zipCode') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'phone') {
      value = value.trim();
    } else if (fieldType === 'municipality') {
      value = value.replace('.', '');
    }

    const validationResult = handleBlur(fieldType, value);

    if (fieldType === 'phone') {
      if (validationResult.isValid) {
        sessionStorage.setItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY, value);
      } else {
        sessionStorage.removeItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY);
      }
    }
  };

  const handleCprValidation = async (ssnValue: string) => {
    setIsCprValidationLoading(true);
    const response = await checkEligibility(ssnValue);
    if (response?.error) {
      logInfoMessage(
        `error - property data - eligibility check - ${response?.message || 'something went wrong'
        } - statusCode: ${response?.statusCode || AppHelper.defaultErrorStatusCode
        }`
      );
      setHasCustomerError(true);
      setErrorObject({
        error: false,
        title: response?.title || AppHelper.defaultErrorTitle,
        message: response?.message || AppHelper.defaultErrorMessage,
        statusCode: response?.statusCode || AppHelper.defaultErrorStatusCode
      });
    } else {
      setHasCustomerError(false);
    }
    setIsCprValidationLoading(false);
  };

  const handleAddressType = (value: string) => {
    setAddressType(value);
  };

  const onCloseErrorModal = () => {
    setIsPropertyDataLoading(false);
    setErrorObject({
      ...errorObject,
      error: false
    });
  };

  const gotoNextStep = async (event: any) => {
    event.preventDefault();

    if (!validateAll(formData)) {
      return;
    }

    setIsPropertyDataLoading(true);

    if (hasCustomerError) {
      setErrorObject(
        (prev) =>
        (prev = {
          ...prev,
          error: true
        })
      );
      return;
    }

    const isPropertyId = addressType === AppHelper.propertyId;
    const FormattedPropertyKey = isPropertyId
      ? collapsePropertyIdWhitespace(collapseWhitespace(formData.propertyKey)).trim()
      : '';
    const formattedMunicipality = isPropertyId
      ? collapseWhitespace(removeKommunPostfix(formData.municipality)).trim()
      : '';
    const formattedAddress = !isPropertyId
      ? collapseWhitespace(formData.address).trim()
      : '';
    const formattedZipCode = !isPropertyId
      ? collapseWhitespace(formData.zipCode).trim()
      : '';
    const propertyId = `${formattedMunicipality} ${FormattedPropertyKey}`;

    const customerInfo = {
      cprNumber: formData.ssn,
      customerAddress: formattedAddress,
      propertyId: isPropertyId ? propertyId : '',
      propertyNameType: addressType,
      zipCode: formattedZipCode
    };

    sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));

    const streetAddressKey = `${formattedAddress} ${formattedZipCode}`;
    const propertyQuery = isPropertyId
      ? propertyId.toUpperCase()
      : streetAddressKey;
    const response = await fetchPropertyData(isPropertyId, propertyQuery);

    if (response?.calculationStatus?.error) {
      setErrorObject({
        error: true,
        title:
          response?.calculationStatus?.title || AppHelper.defaultErrorTitle,
        message:
          response?.calculationStatus?.message || AppHelper.defaultErrorMessage,
        statusCode:
          response?.calculationStatus?.statusCode ||
          AppHelper.defaultErrorStatusCode
      });
      logInfoMessage(
        `error - property data - property fetch - ${response?.calculationStatus?.message || 'something went wrong'
        } - statusCode: ${response?.calculationStatus?.statusCode ||
        AppHelper.defaultErrorStatusCode
        }`
      );
      return;
    }

    if (response?.propertyDataList && response.propertyDataList.length > 0) {
      const property = response.propertyDataList[0];

      const showExtraInfoCaseOne =
        property.addresses === 1 &&
        property.mainBuildings === 2 &&
        property.valueUnits === 1;
      const showExtraInfoCaseTwo =
        property.addresses === 1 &&
        property.mainBuildings === 1 &&
        property.valueUnits === 2;
      const showExtraInfoCaseThree =
        property.addresses === 1 &&
        property.mainBuildings === 2 &&
        property.valueUnits === 2;
      const showExtraInfoToWebshop =
        showExtraInfoCaseOne || showExtraInfoCaseTwo || showExtraInfoCaseThree;

      const CustomerWithPropertyData = {
        ...customerInfo,
        propertyId: property?.propertyIdTextUnique || '',
        customerAddress: property?.address || '',
        zipCode: property?.postNumber ? property.postNumber.toString() : '',
        noOfProperties: showExtraInfoToWebshop ? '2' : '1',
        propertyData: {
          propertyKey: property?.propertyIdText || '',
          area: property?.area ? property?.area.toString() : '',
          builtYear: property?.builtYear ? property?.builtYear.toString() : '',
          ancillaryArea: property?.ancillaryArea
            ? property?.ancillaryArea.toString()
            : ''
        }
      };
      logInfoMessage('success - property data - redirected to webshop');
      sessionStorage.setItem(
        'customerInfo',
        JSON.stringify(CustomerWithPropertyData)
      );
      window.location.href = window.location.origin + AppHelper.webshopUrl;
    } else {
      setErrorObject({
        error: true,
        title: AppHelper.defaultErrorTitle,
        message: AppHelper.defaultErrorMessage,
        statusCode: AppHelper.defaultErrorStatusCode
      });
    }
  };

  const getCprMoreInfoContent = () => {
    return (
      <Text>
        Vi behöver ditt personnummer för att kunna ge dig rätt pris.
        Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
      </Text>
    );
  };

  // To disable phone number in form toggle this to false.
  const showVolontaryPhoneNumber = true;

  const homeParams: CalculationWidgetProps = {
    headerText: enonicData.headerText,
    image: AppHelper.getImageCDNPath(enonicData.imageInfo.image),
    smallScreenImage: AppHelper.getImageCDNPath(
      enonicData.imageInfo.smallScreenImage
    ),
    imageAltText: enonicData.imageInfo.imageAltText,
    inputLists: {
      calculationInputs: [
        {
          type: 'inputText',
          inputListInRow: [
            {
              ...fieldDefinitions.find(f => f.fieldType === 'ssn')!.ui,
              onChange: createChangeHandler('ssn'),
              onBlur: createBlurHandler('ssn'),
              value: formData.ssn,
              invalid: hasFieldError('ssn'),
              errorText: getFieldError('ssn'),
              ref: refs.ssn,
              moreInfoModal: {
                linkText: 'Varför frågar vi om personnummer?',
                title: 'Personnummer',
                content: getCprMoreInfoContent(),
                closeLabel: 'Stäng hjälptext',
                ariaLabel: 'Hjälp om personnummer',
                wrapperClassName: 'cpr-modal-wrapper',
                ariaDescribedBy: 'cpr-more-info'
              }
            }
          ]
        },
        {
          type: 'inputRadio',
          wrapperClassName: 'input-group-row',
          preSelectedValue: AppHelper.streetAddress,
          inputListInRow: [
            {
              id: 'street-address-radio',
              labelText: enonicData.addressLabel,
              name: AppHelper.streetAddress,
              onChange: handleAddressType,
              value: AppHelper.streetAddress,
              labelClassName: 'input-label-text',
              invalid: false,
              errorText: ''
            },
            {
              id: 'property-name-radio',
              labelText: enonicData.propertyNameLabel,
              name: AppHelper.propertyId,
              onChange: handleAddressType,
              value: AppHelper.propertyId,
              labelClassName: 'input-label-text',
              invalid: false,
              errorText: ''
            }
          ]
        },
        {
          type: 'inputText',
          inputListInRow:
            addressType === AppHelper.streetAddress
              ? [
                {
                  ...fieldDefinitions.find(f => f.fieldType === 'address')!.ui,
                  onChange: createChangeHandler('address'),
                  onBlur: createBlurHandler('address'),
                  value: formData.address,
                  invalid: hasFieldError('address'),
                  errorText: getFieldError('address'),
                  ref: refs.address
                }
              ]
              : [
                {
                  ...fieldDefinitions.find(f => f.fieldType === 'municipality')!.ui,
                  onChange: createChangeHandler('municipality'),
                  onBlur: createBlurHandler('municipality'),
                  value: formData.municipality,
                  invalid: hasFieldError('municipality'),
                  errorText: getFieldError('municipality'),
                  ref: refs.municipality
                }
              ]
        },
        {
          type: 'inputText',
          inputListInRow:
            addressType === AppHelper.streetAddress
              ? [
                {
                  ...fieldDefinitions.find(f => f.fieldType === 'zipCode')!.ui,
                  onChange: createChangeHandler('zipCode'),
                  onBlur: createBlurHandler('zipCode'),
                  value: formData.zipCode,
                  invalid: hasFieldError('zipCode'),
                  errorText: getFieldError('zipCode'),
                  ref: refs.zipCode
                }
              ]
              : [
                {
                  ...fieldDefinitions.find(f => f.fieldType === 'propertyKey')!.ui,
                  onChange: createChangeHandler('propertyKey'),
                  onBlur: createBlurHandler('propertyKey'),
                  value: formData.propertyKey,
                  invalid: hasFieldError('propertyKey'),
                  errorText: getFieldError('propertyKey'),
                  ref: refs.propertyKey
                }
              ]
        },
        {
          type: 'inputText',
          isHidden: !showVolontaryPhoneNumber,
          inputListInRow: [
            {
              ...fieldDefinitions.find(f => f.fieldType === 'phone')!.ui,
              onChange: createChangeHandler('phone'),
              onBlur: createBlurHandler('phone'),
              value: formData.phone,
              className: addressType === AppHelper.propertyId ? 'mt-1' : '',
              invalid: hasFieldError('phone'),
              errorText: getFieldError('phone'),
              ref: refs.phone
            }
          ]
        }
      ],
      calculateBtn: {
        btnText: enonicData.calculateBtnLabel,
        onClick: gotoNextStep,
        enableTracking: true,
        disabled: false,
        className:
          addressType === AppHelper.propertyId
            ? 'calculation-button-property-id-input'
            : 'calculation-button mt-1',
        loading: isPropertyDataLoading || isCprValidationLoading
      }
    },
    additionalInfoLinkText: enonicData.personalDataHandlingInfoLinkText,
    additionalInfoLink: enonicData.personalDataHandlinkUrl,
    footerMessage: enonicData.footerMessage
  };

  return (
    <div id="home-villa-calculation-container">
      <CalculationWidget {...homeParams} />
      <PropertyIdExplanationModal
        isOpen={!errorObject.error && propertyIdExplanationModalOpen}
        onCloseExplanationModal={() => setPropertyIdExplanationModalOpen(false)}
        propertyIdExampleIllustration={AppHelper.getImageCDNPath(
          enonicData.imageInfo.propertyIdExampleIllustration
        )}
      />
      <CustomErrorModal
        errorObject={errorObject}
        onCloseErrorModal={onCloseErrorModal}
        propertyIdExampleIllustration={AppHelper.getImageCDNPath(
          enonicData.imageInfo.propertyIdExampleIllustration
        )}
      />
    </div>
  );
};
